import {Plural, Trans} from '@lingui/macro';
import {first} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  DropdownItem,
  DropdownItemProps,
} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {buildContactDisplayName} from '../../../../helpers/models/contactHelpers';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {DropdownItemMultipleContactAvatar} from '../../../common/avatars/dropdownItemMultipleContactAvatar';
import {DropdownItemMultiline} from '../../../common/dropdowns/dropdownItemMultiline';

/*
 * Props.
 */

interface SuggestedVisualParticipantsDropdownItemProps extends DropdownItemProps {
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
}

/*
 * Styles.
 */

const StyledNameDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  grid-gap: 4px;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledContactName = styled.span`
  ${ellipsis()};
`;

const StyledAvatarDiv = styled.div`
  grid-column: 1 / span 2;
  margin: 0px 3px 0px 10px;
  align-self: center;
`;

/*
 * Component.
 */

export const SuggestedVisualParticipantsDropdownItem: FC<SuggestedVisualParticipantsDropdownItemProps> = (
  props,
) => {
  const {visualParticipants} = props;
  const firstVisualParticipant = first(visualParticipants);

  if (!firstVisualParticipant) {
    return null;
  }

  return (
    <DropdownItem {...props} renderTooltip={() => renderTooltip(visualParticipants)}>
      <StyledAvatarDiv>
        <DropdownItemMultipleContactAvatar contacts={visualParticipants} size={18} />
      </StyledAvatarDiv>
      <DropdownItemMultiline
        title={renderName(firstVisualParticipant, visualParticipants.length)}
        subtitle={renderSubtitle()}
      />
    </DropdownItem>
  );
};

function renderTooltip(visualParticipants: ReadonlyArray<VisualParticipantModel>) {
  return visualParticipants.map((p) => <div key={p.contact.uid}>{buildContactDisplayName(p)}</div>);
}

function renderName(firstVisualParticipant: VisualParticipantModel, visualParticipantsLength: number) {
  return (
    <StyledNameDiv>
      <StyledContactName>{buildContactDisplayName(firstVisualParticipant)}</StyledContactName>
      {renderOther(visualParticipantsLength)}
    </StyledNameDiv>
  );
}

function renderOther(visualParticipantsLength: number) {
  // If there are more than two suggested participants, return plural ("others").
  return (
    <Plural
      id="suggested-participants-name-other"
      value={visualParticipantsLength - 1}
      one="and # other"
      other="and # others"
    />
  );
}

function renderSubtitle() {
  return <Trans id="suggested-participants-subtitle">People in this conversation</Trans>;
}
